import styled from "@emotion/styled";
import type { ChangeEvent, ComponentType, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { Option, TestAttribute } from "@skilitics/designer-ui-components";
import { Select, Switch } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { VariableDesign, VariableValue } from "@skilitics/nautilus-core";
import type { VariableConfig, VariableRefId } from "@skilitics/nautilus-core-designer-data";

import { getReferenceId } from "./helpers";
import { useResolveReferenceVariables } from "./useReferenceVariables";

const InputContainer = styled.div`
  width: 100%;
  display: block;
  > div {
    width: 100%;
  }
`;

type VariableKind = "concrete" | "reference" | "either";

export type InitialValueComponentProps<T> = ComponentType<{
  value?: T;
  helperText?: ReactNode;
  label?: ReactNode;
  onChange?: (e: ChangeEvent<HTMLInputElement>, value?: T) => void;
  onChangeCommitted?: (value?: T) => void;
}>;

export type VariableConfigDesignerProps<T extends VariableValue> = TestAttribute & {
  kind: VariableKind;
  nodeId?: number;
  helperText?: ReactNode;
  label?: ReactNode;
  onChange: (newValue?: VariableConfig<T>) => void;
  onChangeCommitted: () => void;
  type: VariableDesign["type"]; // TODO: compare with T
  value?: VariableConfig<T>;
  initialValue?: T;
  initialValueComponent: TestAttribute & InitialValueComponentProps<T>;
};

const getInitialValue = (type: VariableDesign["type"]) => {
  switch (type) {
    case "number":
      return 0;

    case "boolean":
      return false;

    default:
      return "";
  }
};

export const VariableConfigDesigner = <T extends VariableValue>({
  kind,
  helperText,
  label,
  nodeId,
  onChange,
  onChangeCommitted,
  type,
  value,
  initialValue: providedInitialValue,
  initialValueComponent: InitialValueComponent,
  dataTest,
}: VariableConfigDesignerProps<T>) => {
  const resolveReferenceVariables = useResolveReferenceVariables();
  const selectedRefId = getReferenceId(value);
  const formatMessage = useFormatMessage(formatStrings);

  const initialValue: T =
    value && typeof value !== "object" ? value : (providedInitialValue ?? (getInitialValue(type) as T));

  const [usingReference, setUsingReference] = useState(!!selectedRefId);
  const [shadowInitialValue, setShadowInitialValue] = useState(initialValue);
  const [shadowRefId, setShadowRefId] = useState<VariableRefId | "">(selectedRefId);

  const options = useMemo(
    () =>
      nodeId
        ? resolveReferenceVariables(nodeId, type).map(({ title, id }) => ({
            label: title,
            value: id,
          }))
        : [],
    [nodeId, resolveReferenceVariables, type],
  );

  // Check if current shadow ref ID is still valid
  const isShadowRefIdValid = shadowRefId && options.find((option) => option.value === shadowRefId);

  // Use defensive rendering - if shadow ref ID is invalid, fall back to initial state
  const effectiveUsingReference = usingReference && (!!selectedRefId || !!isShadowRefIdValid);
  const effectiveShadowRefId = isShadowRefIdValid ? shadowRefId : "";

  const handleSelectedOptionChange = useCallback(
    ({ value: newValue }: Option) => {
      const refId = options.find((option) => option.value === newValue)?.value as VariableRefId | "" | undefined;
      setShadowRefId(refId ?? "");

      // TODO: variable-config.ts is not optional at this stage.
      if (refId) onChange({ type: "reference", refId });
      onChangeCommitted();
    },
    [options, onChange, onChangeCommitted],
  );

  const handleUseReferenceChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setUsingReference(checked);

      if (!checked) {
        onChange(shadowInitialValue);
      } else {
        // TODO: variable-config.ts is not optional at this stage.
        if (effectiveShadowRefId) onChange({ type: "reference", refId: effectiveShadowRefId });
      }
      onChangeCommitted();
    },
    [onChange, shadowInitialValue, effectiveShadowRefId, onChangeCommitted],
  );

  const handleInitialValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, val?: T) => {
      const newValue = val !== undefined ? val : e.target.value;
      const typedValue: T = (
        type === "number" ? Number(newValue) : type === "boolean" ? Boolean(newValue) : newValue
      ) as T;
      setShadowInitialValue(typedValue);
      onChange(typedValue);
    },
    [onChange, type],
  );

  const InputComponent = (
    <InitialValueComponent
      value={shadowInitialValue}
      onChange={handleInitialValueChange}
      onChangeCommitted={onChangeCommitted}
      label={label}
      helperText={helperText}
      // @ts-ignore
      dataTest={dataTest}
    />
  );

  if (kind === "concrete") return InputComponent;

  return (
    <div>
      {kind === "either" && (
        <Switch
          value={effectiveUsingReference}
          onChange={handleUseReferenceChange}
          checked={effectiveUsingReference}
          label="Reference Other Variable"
          dataTest={dataTest}
        />
      )}

      <InputContainer>
        {kind === "reference" || effectiveUsingReference ? (
          <Select
            label={effectiveShadowRefId ? formatMessage("referenceVariableLabel") : ""}
            options={options}
            value={effectiveShadowRefId}
            onSelectedOptionChange={handleSelectedOptionChange}
            placeholder={formatMessage(options.length ? "referenceVariablePlaceholder" : "noVariablesAvailable")}
            disabled={!options.length}
            dataTest={dataTest}
          />
        ) : (
          InputComponent
        )}
      </InputContainer>
    </div>
  );
};

const formatStrings = {
  referenceVariableLabel: {
    en: "Reference Variable",
  },
  referenceVariablePlaceholder: {
    en: "Select Variable",
  },
  noVariablesAvailable: {
    en: "No Variables Available",
  },
};
